import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';

import { Money } from '../../../../../../api-client';
import { PurchaseConfirmation } from '../../../../../../api-client/models/purchase/PurchaseConfirmation';
import { Button, CloseButton, Loader, PrimaryButton } from '../../../../../../components';
import { PrimaryButtonColor } from '../../../../../../components/common/buttons/decorators/PrimaryButton/PrimaryButton';
import PurchaseConfirmationModal from '../../../../../../components/common/modals/PurchaseConfirmationModal';
import WrapInQuotes from '../../../../../../components/common/typography/texts/WrapInQuotes';
import IFrame from '../../../../../../components/common/utils/IFrame';
import { createOfferPurchaseManager } from '../../../../../../di';
import { useErrorCallback, useNavigateByName } from '../../../../../../hooks';
import useBodyOverflow from '../../../../../../hooks/useBodyOverflow';
import { RouteDictionary } from '../../../../../../utils';

import './PurchaseOfferConfirmation.scoped.scss';

const PurchaseOfferConfirmation = () => {
  const [purchaseConfirmation, setPurchaseConfirmation] = useState<PurchaseConfirmation | null>(
    null
  );
  const handleError = useErrorCallback();
  // const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const { offerUuid, confirmationUuid } = useParams();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigateByName();

  useBodyOverflow();

  const portalTarget = useMemo(() => {
    return document.getElementById('modal-container') ?? document.body;
  }, []);

  useEffect(() => {
    const requestConfirmation = async () => {
      setLoading(true);
      try {
        const manager = await createOfferPurchaseManager();
        const result = await manager.getConfirmation(offerUuid!, confirmationUuid!);
        setPurchaseConfirmation(result);
      } catch (err: any) {
        await handleError(err);
        setPurchaseConfirmation(null);
      } finally {
        setLoading(false);
      }
    };
    (async () => {
      await requestConfirmation();
    })();
  }, []);

  const handleSubmit = async (): Promise<void> => {
    setLoading(true);
    try {
      const manager = await createOfferPurchaseManager();
      await manager.sendCode(purchaseConfirmation!.offerUuid, confirmationUuid!);
      setCodeSent(true);
    } catch (err: any) {
      await handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const closePage = () => {
    navigate(RouteDictionary.PURCHASE_OFFER, { offerUuid: offerUuid! });
  };

  return createPortal(
    <div className="investment-confirmation">
      <div className="investment-confirmation__close">
        <CloseButton>
          <Button onClick={closePage} />
        </CloseButton>
      </div>
      <div className="investment-confirmation__content">
        <div className="investment-confirmation__block">
          <div className="investment-confirmation__header">
            Агентское поручение на заключение сделки №{}
          </div>
          <div className="investment-confirmation__text">
            {loading && <Loader />}
            {purchaseConfirmation && (
              <>
                <div className="investment-confirmation__document">
                  <IFrame src={purchaseConfirmation.agreementUrl} />
                </div>
                <div>
                  <PrimaryButton expanded large color={PrimaryButtonColor.GREEN}>
                    <Button onClick={handleSubmit}>Подтвердить</Button>
                  </PrimaryButton>
                </div>
                <div>
                  {/*<Checkbox*/}
                  {/*    id="accept-agreement"*/}
                  {/*    checked={agreementAccepted}*/}
                  {/*    onChange={() => setAgreementAccepted((accepted) => !accepted)}*/}
                  {/*>*/}
                  {/*    Согласен с <ColoredText color={TextColor.PRIMARY_GREEN}>*/}
                  {/*    <a href="#">условиями использования</a></ColoredText> использования*/}
                  {/*    платформы и <ColoredText color={TextColor.PRIMARY_GREEN}>*/}
                  {/*    <a href="#">обработки</a>*/}
                  {/*</ColoredText> персональных данных*/}
                  {/*</Checkbox>*/}
                </div>
                <div className="investment-confirmation__info">
                  <div>
                    Подписанием настоящего агентского поручения Принципал подтверждает факт своего
                    предварительного ознакомления со всеми локальными актами Площадки, размещенными
                    в информационно-коммуникационной сети <WrapInQuotes>Интернет</WrapInQuotes>
                    на сайте: cabinet.conomica.ru и действующими на момент подписания, и
                    подтверждает свое согласие выполнять требования указанных локальных актов
                    Площадки.
                  </div>
                  <div>
                    Подписание настоящего агентского поручения осуществляется Принципалом и
                    фиксируется в электронной форме с использованием информационных
                  </div>
                </div>
              </>
            )}
          </div>
          <PurchaseConfirmationModal
            active={codeSent}
            onClose={() => setCodeSent(false)}
            purchaseConfirmation={{
              offerUuid: offerUuid!,
              confirmationUuid: confirmationUuid!,
              money: new Money(0, 'RUB'),
            }}
          />
        </div>
      </div>
    </div>,
    portalTarget
  );
};

export default PurchaseOfferConfirmation;
